import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  MapPin, GraduationCap, Loader2, UserPlus, Check, X, MoreVertical,
  Flag, Ban, Clock, ShieldCheck, Compass, MessageCircle, UserMinus, Sparkles, Share2,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { UI, t } from "@/i18n/translations";
import { api } from "@/lib/api";
import { langLabel } from "@/data/languages";
import { Messages, distanceLabel } from "@/pages/Messages";

const monthsLabel = (n, lang) => (lang === "fr" ? "mois" : n === 1 ? "month" : "months");
const monthsInLabel = (n, lang) => (lang === "fr" ? "mois en France" : `${n === 1 ? "month" : "months"} in France`);
const RADII = [2, 5, 10, 0];

export default function Reseau() {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const [tab, setTab] = useState("buddies");
  const [meInfo, setMeInfo] = useState(null);
  const [unread, setUnread] = useState(0);

  const loadMe = useCallback(() => {
    api.get("/reseau/me").then(({ data }) => setMeInfo(data)).catch(() => {});
  }, []);
  const loadUnread = useCallback(() => {
    api.get("/reseau/unread").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, []);
  useEffect(() => { loadMe(); loadUnread(); }, [loadMe, loadUnread]);
  useEffect(() => {
    const iv = setInterval(loadUnread, 25000);
    return () => clearInterval(iv);
  }, [loadUnread]);

  const toggleAvail = async () => {
    const next = !meInfo.buddy_available;
    setMeInfo((m) => ({ ...m, buddy_available: next }));
    try {
      await api.put("/reseau/availability", { available: next });
    } catch {
      setMeInfo((m) => ({ ...m, buddy_available: !next }));
    }
  };

  const TABS = [
    { key: "buddies", label: t(N.tabBuddies, lang) },
    { key: "newcomers", label: t(N.tabNewcomers, lang) },
    { key: "requests", label: t(N.tabRequests, lang) },
    { key: "messages", label: t(N.tabMessages, lang), badge: unread },
  ];

  return (
    <div className="animate-fade-up space-y-5">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{t(N.title, lang)}</h1>
        <p className="text-sm text-ink/60 mt-1">{t(N.intro, lang)}</p>
      </div>

      {/* Status card */}
      {meInfo && (
        <div className="rounded-[18px] bg-ink text-paper p-5 shadow-sm" data-testid="reseau-status">
          <div className="flex items-center gap-3">
            <div className="grid place-items-center w-11 h-11 rounded-full bg-amber/20">
              <Compass className="w-6 h-6 text-amber" />
            </div>
            <div className="flex-1">
              <div className="font-display text-lg font-bold">
                {meInfo.buddy_eligible ? t(N.statusBuddy, lang) : t(N.statusNouvel, lang)}
              </div>
              {meInfo.mois_en_france != null && (
                <div className="text-sm text-paper/60">{meInfo.mois_en_france} {monthsInLabel(meInfo.mois_en_france, lang)}</div>
              )}
            </div>
          </div>

          {meInfo.buddy_eligible ? (
            <div className="mt-4 pt-4 border-t border-paper/15">
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm font-medium">{t(N.availableToggle, lang)}</span>
                <button
                  data-testid="availability-toggle"
                  onClick={toggleAvail}
                  role="switch"
                  aria-checked={meInfo.buddy_available}
                  className={`relative w-12 h-7 rounded-full transition-colors ${meInfo.buddy_available ? "bg-sage" : "bg-paper/25"}`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${meInfo.buddy_available ? "left-6" : "left-1"}`} />
                </button>
              </label>
              <p className="text-xs text-paper/50 mt-1.5">{t(N.availableHint, lang)}</p>
            </div>
          ) : (
            <p className="text-xs text-paper/60 mt-3 pt-3 border-t border-paper/15">{t(N.eligibleSoon, lang)}</p>
          )}
          {meInfo.has_location === false && (
            <p className="text-xs text-amber mt-2">{t(N.noLocation, lang)}</p>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 rounded-2xl bg-white border border-line p-1 overflow-x-auto no-scrollbar">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            data-testid={`reseau-tab-${tb.key}`}
            onClick={() => { setTab(tb.key); if (tb.key === "messages") loadUnread(); }}
            className={`relative flex-1 whitespace-nowrap rounded-xl py-2 px-2.5 text-xs font-bold transition-colors ${
              tab === tb.key ? "bg-amber text-ink" : "text-ink/55 hover:text-ink"
            }`}
          >
            {tb.label}
            {tb.badge > 0 && (
              <span className="ml-1 inline-grid place-items-center min-w-4 h-4 px-1 rounded-full bg-coral text-white text-[10px] align-middle" data-testid="messages-badge">{tb.badge}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "requests" ? (
        <Requests onChange={() => { loadMe(); loadUnread(); }} onOpenMessages={() => setTab("messages")} />
      ) : tab === "messages" ? (
        <Messages onChanged={loadUnread} />
      ) : (
        <Discover kind={tab} onChanged={loadUnread} />
      )}

      <p className="flex items-start gap-2 text-xs text-ink/50 leading-relaxed pt-1">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-sage" />
        {t(N.privacy, lang)}
      </p>
    </div>
  );
}

function Discover({ kind, onChanged }) {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [ecole, setEcole] = useState("");
  const [langue, setLangue] = useState("");
  const [radius, setRadius] = useState(0);
  const [sentIds, setSentIds] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { kind };
    if (ecole) params.ecole = ecole;
    if (langue) params.langue = langue;
    if (radius) params.radius = radius;
    api.get("/reseau/discover", { params })
      .then(({ data }) => setResults(data.results || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [kind, ecole, langue, radius]);

  useEffect(() => { load(); }, [load]);

  const ecoles = [...new Set(results.map((r) => r.ecole).filter(Boolean))].map((o) => ({ value: o, label: o }));
  const langues = [...new Set(results.flatMap((r) => r.langues_parlees || []))].map((c) => ({ value: c, label: langLabel(c, lang) }));

  const connect = async (id) => {
    setSentIds((s) => [...s, id]);
    try {
      await api.post("/reseau/connect", { target_user_id: id });
      toast.success(t(N.connectSent, lang));
    } catch (e) {
      setSentIds((s) => s.filter((x) => x !== id));
      toast.error(e.response?.data?.detail || UI.common.error[lang]);
    }
  };

  const invite = async () => {
    const url = window.location.origin;
    const text = `${t(N.inviteText, lang)} ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Boussole", text: t(N.inviteText, lang), url });
        return;
      }
    } catch { /* user cancelled share */ }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(N.inviteCopied, lang));
    } catch {
      toast.success(t(N.inviteCopied, lang));
    }
  };

  const moderate = async (action, id) => {
    try {
      await api.post(`/reseau/${action}`, { target_user_id: id });
      const msg = action === "block" ? N.blocked : action === "end" ? N.ended : N.reported;
      toast.success(t(msg, lang));
      if (action === "block" || action === "end") setResults((r) => r.filter((x) => x.user_id !== id));
      onChanged?.();
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  if (loading) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;

  return (
    <div className="space-y-4">
      {/* Radius — proximity is the primary sort */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
        <span className="text-xs font-semibold text-ink/50 shrink-0">{t(N.radius, lang)}</span>
        {RADII.map((r) => (
          <button
            key={r}
            data-testid={`radius-${r}`}
            onClick={() => setRadius(r)}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${
              radius === r ? "bg-ink text-paper border-ink" : "bg-white border-line text-ink/60 hover:border-amber"
            }`}
          >
            {r === 0 ? t(N.radiusAll, lang) : t(N[`radius${r}`], lang)}
          </button>
        ))}
      </div>

      {(ecoles.length > 0 || langues.length > 0) && (
        <div className="flex gap-2">
          <FilterSelect testId="filter-ecole" label={t(N.filterEcole, lang)} value={ecole} onChange={setEcole} options={ecoles} allLabel={t(N.filterAll, lang)} />
          <FilterSelect testId="filter-langue" label={t(N.filterLangue, lang)} value={langue} onChange={setLangue} options={langues} allLabel={t(N.filterAll, lang)} />
        </div>
      )}

      {results.length === 0 ? (
        <div className="rounded-[18px] border border-dashed border-line bg-white/50 p-8 text-center" data-testid="reseau-empty">
          <span className="mx-auto grid place-items-center w-12 h-12 rounded-2xl bg-amber/15 mb-3">
            <Sparkles className="w-6 h-6 text-amber" strokeWidth={2.2} />
          </span>
          <h3 className="font-display text-lg font-bold mb-1">{t(N.pioneerTitle, lang)}</h3>
          <p className="text-sm text-ink/60 mb-4">{t(N.pioneerBody, lang)}</p>
          <button
            data-testid="invite-btn"
            onClick={invite}
            className="rounded-2xl bg-amber text-ink font-bold px-5 py-3 inline-flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" /> {t(N.inviteBtn, lang)}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="discover-list">
          {results.map((p) => (
            <ProfileCard key={p.user_id} p={p} onConnect={connect} onModerate={moderate} sent={sentIds.includes(p.user_id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileCard({ p, onConnect, onModerate, sent }) {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const [menu, setMenu] = useState(false);

  return (
    <div className="rounded-[18px] bg-white border border-line p-4 shadow-sm relative" data-testid={`profile-${p.user_id}`}>
      <div className="flex items-start gap-3">
        <div className="grid place-items-center w-11 h-11 rounded-full bg-amber/15 shrink-0 overflow-hidden">
          {p.picture ? <img src={p.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold text-amber">{(p.prenom || "?")[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-base font-bold">{p.prenom}</span>
            {p.buddy_eligible && (
              <span className="rounded-full bg-sage/15 text-sage text-[10px] font-extrabold px-2 py-0.5 tracking-wide">{t(N.badge, lang)}</span>
            )}
          </div>
          {(p.match_school || p.match_city) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`match-badges-${p.user_id}`}>
              {p.match_school && (
                <span data-testid={`badge-school-${p.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber/15 text-amber text-[11px] font-bold px-2 py-0.5">
                  <GraduationCap className="w-3 h-3" /> {t(N.matchSchool, lang)}{p.match_school_name ? ` · ${p.match_school_name}` : ""}
                </span>
              )}
              {p.match_city && (
                <span data-testid={`badge-city-${p.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-sage/15 text-sage text-[11px] font-bold px-2 py-0.5">
                  <MapPin className="w-3 h-3" />
                  {p.distance_km != null && p.distance_km <= 20 ? `${t(N.matchNear, lang)} (~${p.distance_km.toFixed(1)} km)` : t(N.matchCity, lang)}
                </span>
              )}
            </div>
          )}
          <div className="text-xs text-ink/55 mt-1 space-y-0.5">
            {(p.quartier || p.ville) && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                {[p.quartier, p.ville].filter(Boolean).join(", ")}
                <span className="text-ink/35">· {distanceLabel(p.distance_km, lang)}</span>
              </div>
            )}
            {p.ecole && <div className="flex items-center gap-1.5"><GraduationCap className="w-3.5 h-3.5" />{p.ecole}</div>}
            {p.mois_en_france != null && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {t(N.arrivedAgo, lang)} {p.mois_en_france} {monthsLabel(p.mois_en_france, lang)} {t(N.arrivedAgoSuffix, lang)}
              </div>
            )}
          </div>
          {(p.langues_parlees?.length > 0 || p.centres_interet?.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[...(p.langues_parlees || []).map((c) => langLabel(c, lang)), ...(p.centres_interet || [])].slice(0, 6).map((tag) => (
                <span key={tag} className="rounded-full bg-paper border border-line text-ink/70 text-[11px] font-medium px-2 py-0.5">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <button data-testid={`menu-${p.user_id}`} onClick={() => setMenu((m) => !m)} className="p-1 text-ink/40 hover:text-ink">
          <MoreVertical className="w-5 h-5" />
        </button>
        {menu && (
          <div className="absolute right-3 top-11 z-10 rounded-xl border border-line bg-white shadow-lg overflow-hidden w-40">
            <button data-testid={`unfollow-${p.user_id}`} onClick={() => { setMenu(false); onModerate("end", p.user_id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-paper">
              <UserMinus className="w-4 h-4 text-ink/60" /> {t(N.unfollow, lang)}
            </button>
            <button data-testid={`report-${p.user_id}`} onClick={() => { setMenu(false); onModerate("report", p.user_id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-paper">
              <Flag className="w-4 h-4 text-ink/60" /> {t(N.report, lang)}
            </button>
            <button data-testid={`block-${p.user_id}`} onClick={() => { setMenu(false); onModerate("block", p.user_id); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-coral hover:bg-paper">
              <Ban className="w-4 h-4" /> {t(N.block, lang)}
            </button>
          </div>
        )}
      </div>

      <button
        data-testid={`connect-${p.user_id}`}
        onClick={() => onConnect(p.user_id)}
        disabled={sent}
        className={`w-full mt-3 rounded-2xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 transition ${
          sent ? "bg-sage/15 text-sage" : "bg-amber text-ink hover:brightness-105 active:scale-[0.99]"
        }`}
      >
        {sent ? <><Check className="w-4 h-4" /> {t(N.connectSent, lang)}</> : <><UserPlus className="w-4 h-4" /> {t(N.connect, lang)}</>}
      </button>
    </div>
  );
}

function Requests({ onChange, onOpenMessages }) {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const [data, setData] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/reseau/requests").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (conn_id, action) => {
    try {
      await api.post(`/reseau/requests/${conn_id}/respond`, { action });
      load();
      onChange?.();
    } catch {
      toast.error(UI.common.error[lang]);
    }
  };

  if (loading) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber" /></div>;

  const empty = data.sent.length === 0 && data.received.length === 0;
  if (empty) return <div className="rounded-[18px] border border-dashed border-line bg-white/50 p-8 text-center text-sm text-ink/50">{t(N.noRequests, lang)}</div>;

  const openChatBtn = (r) => r.status === "accepted" && (
    <button data-testid={`open-chat-${r.conn_id}`} onClick={onOpenMessages} className="w-full mt-3 rounded-2xl bg-ink text-paper font-bold text-sm py-2.5 flex items-center justify-center gap-2">
      <MessageCircle className="w-4 h-4" /> {t(N.openChat, lang)}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="requests-list">
      {data.received.length > 0 && (
        <div>
          <h3 className="font-display text-base font-bold mb-2">{t(N.received, lang)}</h3>
          <div className="space-y-3">
            {data.received.map((r) => (
              <RequestRow key={r.conn_id} r={r} actions={<>
                {r.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <button data-testid={`accept-${r.conn_id}`} onClick={() => respond(r.conn_id, "accept")} className="flex-1 rounded-2xl bg-sage text-white font-bold text-sm py-2.5 flex items-center justify-center gap-1.5"><Check className="w-4 h-4" />{t(N.accept, lang)}</button>
                    <button data-testid={`decline-${r.conn_id}`} onClick={() => respond(r.conn_id, "decline")} className="flex-1 rounded-2xl border border-line font-bold text-sm py-2.5 flex items-center justify-center gap-1.5 text-ink/70"><X className="w-4 h-4" />{t(N.decline, lang)}</button>
                  </div>
                )}
                {openChatBtn(r)}
              </>} />
            ))}
          </div>
        </div>
      )}
      {data.sent.length > 0 && (
        <div>
          <h3 className="font-display text-base font-bold mb-2">{t(N.sent, lang)}</h3>
          <div className="space-y-3">
            {data.sent.map((r) => <RequestRow key={r.conn_id} r={r} actions={openChatBtn(r)} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function RequestRow({ r, actions }) {
  const { lang } = useLang();
  const N = UI.reseauMod;
  const statusMap = { pending: N.pending, accepted: N.accepted, declined: N.declined };
  const statusColor = { pending: "text-amber bg-amber/10", accepted: "text-sage bg-sage/10", declined: "text-ink/50 bg-ink/5" }[r.status];
  return (
    <div className="rounded-[18px] bg-white border border-line p-4" data-testid={`request-${r.conn_id}`}>
      <div className="flex items-center gap-3">
        <div className="grid place-items-center w-10 h-10 rounded-full bg-amber/15 shrink-0 overflow-hidden">
          {r.picture ? <img src={r.picture} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-bold text-amber">{(r.prenom || "?")[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-display font-bold">{r.prenom}</span>
            {r.buddy_eligible && <span className="rounded-full bg-sage/15 text-sage text-[10px] font-extrabold px-2 py-0.5">{t(N.badge, lang)}</span>}
          </div>
          <div className="text-xs text-ink/50">{[r.quartier, r.ville].filter(Boolean).join(", ")}</div>
        </div>
        <span className={`rounded-full text-[11px] font-bold px-2.5 py-1 ${statusColor}`}>{t(statusMap[r.status], lang)}</span>
      </div>
      {actions}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, allLabel, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 rounded-2xl border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-amber"
    >
      <option value="">{label} · {allLabel}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
